import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import AdminSidebar from "./AdminSidebar";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [totalEarned, setTotalEarned] = useState(0);
  const [salesData, setSalesData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    const adminVariable = localStorage.getItem("admin");
      if (!adminVariable) throw new Error("Pagina non trovata");

  const fetchOrders = async () => {
    try {
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;

      const response = await fetch(`${host}:${port}/api/v1/orders/get_all_orders`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.status === 'OK') {
        const orders = data.data;

        // ✅ Calcolo totale guadagnato SOLO con ordini 'succeeded'
        const total = orders
          .filter(order => order.status === 'succeeded')
          .reduce((sum, order) => sum + parseFloat(order.amount), 0);
        setTotalEarned(total);

        // Dati per grafico: vendite mensili (tutte le vendite, o solo "succeeded"?)
        const salesPerMonth = {};
        orders.forEach(order => {
          if (order.status !== 'succeeded') return;
          const date = new Date(order.order_date * 1000);
          const month = date.toLocaleString('default', { month: 'short' });
          salesPerMonth[month] = (salesPerMonth[month] || 0) + parseFloat(order.amount);
        });

        const labels = Object.keys(salesPerMonth);
        const values = Object.values(salesPerMonth);

        setSalesData({
          labels,
          datasets: [
            {
              label: "Vendite",
              data: values,
              borderColor: "#94c41c",
              backgroundColor: "rgba(148, 196, 28, 0.2)",
              fill: true,
            }
          ]
        });

        const sortedOrders = [...orders].sort((a, b) => b.order_date - a.order_date);
        setOrders(sortedOrders);

      } else {
        console.error('Errore nel recupero degli ordini:', data);
      }
    } catch (err) {
      console.error('Errore:', err);
    }
  };

  fetchOrders();
}, []);

  const salesOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: { enabled: true }
    }
  };

  // Prendo solo ultimi 5 ordini per la lista pagamenti
  const lastFivePayments = orders.slice(0, 5);

  // Prendo solo ultimi 5 domini comprati per la terza card
  // Verifico se order ha il campo dominio in uno di questi modi:
  // order.domain oppure order.domain_name
  const lastFiveDomains = orders
    .filter(order => order.domain || order.domain_name)
    .slice(0, 5);

  // Funzione helper per calcolare tempo "X ago"
  const timeAgo = (unixTimestamp) => {
    const now = new Date();
    const date = new Date(unixTimestamp * 1000);
    const diffSec = Math.floor((now - date) / 1000);

    if (diffSec < 60) return `${diffSec} secondi fa`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minuti fa`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ore fa`;
    return `${Math.floor(diffSec / 86400)} giorni fa`;
  };

  // Funzione helper per mostrare nome utente (nome + cognome o company)
  const getUserLabel = (order) => {
    if (order.name && order.surname) return `${order.name} ${order.surname}`;
    if (order.company) return order.company;
    return "Anonimo";
  };

  return (
    <div className="mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
      <AdminSidebar className="bg-white shadow-md p-5 md:w-64 order-0 md:order-0" />

      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 xl:grid-cols-2 gap-5 ml-4 xl:-ml-[15%] lg:ml-0">

        {/* CARD 1: GRAFICO + TOTALE */}
        <div className="bg-white shadow-lg p-4 max-w-[90%] w-full my-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Grafico delle Vendite</h3>
          <p className="text-2xl font-bold text-[#94c41c] mb-3">
            Totale guadagnato: ${totalEarned.toFixed(2)}
          </p>
          <Line data={salesData} options={salesOptions} />
        </div>

        {/* CARD 2: ULTIMI 5 PAGAMENTI + VEDI ALTRO */}
        <div className="bg-white shadow-lg p-4 max-w-[90%] w-full my-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Ultimi Pagamenti</h3>
          <ul className="space-y-3">
            {lastFivePayments.map(payment => (
              <li
                key={payment.id_order}
                className="flex justify-between items-center p-3 rounded-lg border border-gray-300 text-sm"
              >
                <span className="text-green-800 font-medium">{getUserLabel(payment)}</span>
                <div className="flex-1 hidden min-[400px]:flex justify-between px-4">
                  <span className="text-gray-800">${parseFloat(payment.amount).toFixed(2)}</span>
                  <span className="text-gray-500">{timeAgo(payment.order_date)}</span>
                </div>
                <span
                  className={`font-semibold text-xs px-2 py-1 rounded-full ${
                    payment.status === "succeeded"
                      ? "bg-[#94c41c] text-white"
                      : payment.status === "pending"
                      ? "bg-yellow-200 text-yellow-800"
                      : "bg-red-200 text-red-800"
                  }`}
                >
                  {payment.status === "succeeded"
                    ? "Successo"
                    : payment.status === "pending"
                    ? "In Attesa"
                    : "Negato"}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end mt-3">
            <a
              href="/admin-payments"
              className="flex items-center text-[#94c41c] font-semibold hover:underline"
            >
              Vedi altro
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* CARD 3: DOMINI RECENTI */}
        <div className="bg-white shadow-lg p-4 max-w-[90%] w-full my-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Domini Recentemente Comprati</h3>
          {lastFiveDomains.length === 0 ? (
            <p className="text-gray-500 text-sm">Nessun dominio acquistato di recente.</p>
          ) : (
            <ul className="space-y-3">
              {lastFiveDomains.map(order => (
                <li
                  key={order.id_order}
                  className="flex justify-between items-center p-3 rounded-lg border border-gray-300 text-sm"
                >
                  <span className="text-green-600 font-medium">{order.domain || order.domain_name}</span>
                  <span className="text-gray-800">${parseFloat(order.amount).toFixed(2)}</span>
                  <span className="text-gray-600">{getUserLabel(order)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* CARD 4: STATISTICHE */}
        <div className="bg-white shadow-lg p-4 max-w-[90%] w-full my-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Statistiche Varie</h3>
          <p className="text-gray-600 text-sm">Altre statistiche sui domini e utenti attivi.</p>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
